/**
 * VAL-68-OWNER-RULINGS R3 — "Your value" for an owner whose option is Value at a Glance or
 * Value + Drivers. The server sends only what the option includes (`owner_view`): the value,
 * its low-high range and the summary sentence, plus the value drivers for Value + Drivers.
 * VAL-R3-RANGE-ALL (owner 10-31 05:35, "Show range in every option"): the range is shown here
 * too. No approaches, multiples or comparison reach this screen for those options. Every word
 * here is already approved (the owner's value title, "Range" from the Value Snapshot document,
 * "Value drivers"); no new client text.
 */
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import type { RecomputeResponse } from '@/types/valuationB3'
import { money } from '@/utils/format'
import { Fact } from './_shared'
import { humanizeDriver } from './_IntakeDrivers'
import { PaidOn } from './_ValueBlocked'

export function OptionValue({ run, paidAt }: { run: RecomputeResponse; paidAt?: string | null }) {
  const lines = run.questionnaire?.lines ?? []
  const hasRange = typeof run.value_low === 'number' && typeof run.value_high === 'number'
  return (
    <>
      <Card data-testid="option-value">
        <CardHeader>
          <CardTitle>Owner's Estimated Value</CardTitle>
          <PaidOn paidAt={paidAt} />
        </CardHeader>
        <CardContent>
          <p className="text-3xl font-bold tracking-tight text-pal-navy">{money(run.calculated_value)}</p>
          {hasRange && (
            <div data-testid="option-range" className="mt-3">
              <Fact label="Range" value={`${money(run.value_low)} – ${money(run.value_high)}`} mono />
            </div>
          )}
          {run.positioning && <p className="mt-3 text-sm text-pal-ink-body">{run.positioning}</p>}
        </CardContent>
      </Card>
      {run.owner_view === 'value_drivers' && lines.length > 0 && (
        <Card data-testid="option-drivers">
          <CardHeader><CardTitle>Value drivers</CardTitle></CardHeader>
          <CardContent>
            {lines.map((l, i) => (
              <Fact key={i} label={humanizeDriver(l.driver)} value={`${l.score} / ${l.max_score}`} />
            ))}
          </CardContent>
        </Card>
      )}
    </>
  )
}
